import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { Row, Col } from "react-bootstrap";
import { orderBy } from "lodash";
import { Link } from "react-router-dom";
import { IoArrowRedo } from "react-icons/io5";
import { Tooltip as ReactTooltip } from "react-tooltip";

import DropdownSearchSelect from "./DropdownSearchSelect";

import TimesheetInput from "./TimesheetInput";

const TimesheetRow = ({
  customers,
  projects,
  contracts,
  auth,
  row,
  idx,
  onChange,
  onChangeInput,
  getServiceOptions,
  onEnterCallDetails,
  onShowProjectDetails
}) => {
  const disabled = useMemo(
    () =>
      !!row.calls.find(
        (call) =>
          (call.stage && call.stage.order > 4) ||
          (auth.maxRole < 7000 &&
            call.assigned_to &&
            call.assigned_to._id !== auth.user._id) ||
          (call.project && call.project.name === "Personal Time")
      ),
    [auth, row]
  );

  const customerSelectData = useMemo(
    () =>
      orderBy(
        customers.map((customer) => ({
          value: customer._id,
          label: customer.name
        })),
        [(option) => option.label.toLowerCase()]
      ),
    [customers]
  );

  // if this one runs into issues there may be a problem with having two iterating functions works fine now -RW 8/21
  const projectSelectData = useMemo(
    () =>
      orderBy(
        projects
          .filter(
            (proj) =>
              proj.customer &&
              proj.customer._id === row[`customer ${idx}`] &&
              proj.project_status.value !== "CAN"
          )
          .map((proj) => ({
            value: proj._id,
            label: proj.name
          })),
        [(option) => option.label.toLowerCase()]
      ),
    [idx, projects, row]
  );

  const serviceSelectData = useMemo(
    () => getServiceOptions(idx),
    [idx, getServiceOptions]
  );

  const supportTicket = useMemo(
    () =>
      row.calls.find((item) => typeof item.support_ticket_id === "string")
        ?.support_ticket,
    [row]
  );

  const serviceDisabled = useMemo(
    () => row.calls.find((call) => call.service),
    [row]
  );

  const callWithContract = useMemo(
    () => row.calls.filter((call) => call.contract),
    [row.calls]
  );

  const contract = useMemo(
    () => contracts.find((c) => c._id === callWithContract?.[0]?.contract),
    [callWithContract, contracts]
  );

  return (
    <Row
      key={row.id}
      style={{ marginRight: 0, marginLeft: 0 }}
      className={supportTicket ? "time-row-ticket" : "time-row"}
    >
      <Col md={2} xs={2} className="time-select">
        <DropdownSearchSelect
          name={`customer ${idx}`}
          value={row[`customer ${idx}`]}
          onChange={onChange}
          disabled={disabled}
          data={customerSelectData}
        />
      </Col>
      <Col md={2} xs={2} className="time-select time-select--project">
        <DropdownSearchSelect
          onOptionClick={() =>
            row[`project ${idx}`]
              ? onShowProjectDetails(row[`project ${idx}`])
              : {}
          }
          name={`project ${idx}`}
          value={row[`project ${idx}`] ? row[`project ${idx}`]._id : ""}
          onChange={onChange}
          disabled={disabled}
          data={projectSelectData}
        />
      </Col>
      <Col md={2} xs={2} className="time-select">
        <DropdownSearchSelect
          name={`service ${idx}`}
          value={row[`service ${idx}`] || ""}
          onChange={onChange}
          disabled={disabled || serviceDisabled}
          data={serviceSelectData}
        />
      </Col>
      <Col md={6} xs={6} className="time-hours">
        {row.calls.map((call, i) => (
          <TimesheetInput
            key={i}
            rowIdx={idx}
            call={call}
            auth={auth}
            disabled={
              !row[`service ${idx}`] ||
              !row[`project ${idx}`] ||
              !projectSelectData.length
            }
            onChangeInput={onChangeInput}
            onEnterCallDetails={(event, rowIdx) =>
              onEnterCallDetails(event, rowIdx, supportTicket?._id)
            }
          />
        ))}
        <span className="hour-container">
          <input
            type="number"
            className="form-control time-field"
            name={`total ${idx}`}
            readOnly
            disabled
            value={
              typeof row[`total ${idx}`] === "number"
                ? row[`total ${idx}`].toFixed(1)
                : 0
            }
          />
        </span>
      </Col>
      {supportTicket && (
        <Link
          to={`/app/support-tickets/${supportTicket._id}`}
          style={{
            paddingLeft: 10,
            fontSize: "small"
          }}
          id={`info-${supportTicket?._id}-${row.id}`}
          data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
        >
          {`View Ticket #${supportTicket.support_ticket_number}`}
          <IoArrowRedo style={{ paddingLeft: "3px" }} />
        </Link>
      )}
      {supportTicket && contract?.tier?.label && (
        <span className="red-text">{`${contract?.tier?.label}`}</span>
      )}

      <ReactTooltip
        anchorId={`info-${supportTicket?._id}-${row.id}`}
        className={`custom-tooltip ${auth.isNightMode ? "dark" : "light"}`}
        place="right"
        type={auth.isNightMode ? "dark" : "light"}
        effect="solid"
        globalEventOff="click"
      >
        <React.Fragment>
          <b>{supportTicket?.requestor_full_name}: </b>
          {supportTicket?.request_nature_details}
        </React.Fragment>
      </ReactTooltip>
    </Row>
  );
};

TimesheetRow.propTypes = {
  customers: PropTypes.array,
  projects: PropTypes.array,
  contracts: PropTypes.array,
  auth: PropTypes.object,
  row: PropTypes.object,
  idx: PropTypes.number,
  onChange: PropTypes.func,
  getServiceOptions: PropTypes.func,
  onChangeInput: PropTypes.func,
  onEnterCallDetails: PropTypes.func,
  onShowProjectDetails: PropTypes.func
};
export default TimesheetRow;
